import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, ArrowRight, MessageCircle, Zap, Brain, Video, 
  Sparkles, Image, Mic, Code, Palette, Film, Smartphone
} from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../../components/Navigation";

const WHATSAPP_LINK = "https://wa.me/33644692901?text=Bonjour%20!%20Je%20souhaite%20une%20formation%20sur%20les%20outils%20Google%20AI.";

const googleTools = [
  {
    name: "Google AI Studio",
    icon: Brain,
    emoji: "🧠",
    description: "Environnement pour que les développeurs testent et perfectionnent leurs prompts.",
    useCase: "Idéal pour créer des applications IA personnalisées et tester des modèles."
  },
  {
    name: "NotebookLM",
    icon: Sparkles,
    emoji: "📚",
    description: "Convertit documents et notes en résumés ou cartes mentales intelligentes.",
    useCase: "Parfait pour la recherche, la veille et la synthèse de documents longs."
  },
  {
    name: "Gemini Gems",
    icon: Sparkles,
    emoji: "💎",
    description: "Permet de créer des versions personnalisées de l'IA pour des tâches spécifiques.",
    useCase: "Créez des assistants IA sur mesure pour votre métier."
  },
  {
    name: "Google Veo 3",
    icon: Video,
    emoji: "🎬",
    description: "Génère des clips vidéo de haute qualité avec une esthétique cinématographique à partir de texte.",
    useCase: "Production vidéo automatisée pour marketing et communication."
  },
  {
    name: "Firebase Studio",
    icon: Code,
    emoji: "🔥",
    description: "Facilite la création d'apps et de sites web boostés par l'IA sans complications techniques.",
    useCase: "Développement rapide d'applications métier sans coder."
  },
  {
    name: "Gemini Live",
    icon: Mic,
    emoji: "🎙️",
    description: "IA qui participe en temps réel aux réunions en tant qu'assistant ou coprésentateur.",
    useCase: "Assistant de réunion intelligent, prise de notes automatique."
  },
  {
    name: "NanoBanana",
    icon: Image,
    emoji: "🍌",
    description: "Outil pour éditer et générer de multiples variations d'une même image.",
    useCase: "Création de visuels marketing, tests A/B visuels."
  },
  {
    name: "Google Imagen",
    icon: Palette,
    emoji: "🎨",
    description: "Crée des images et des clips audio de haute qualité avec des prompts simples.",
    useCase: "Génération d'images professionnelles pour tous supports."
  },
  {
    name: "Google Pomelli",
    icon: Sparkles,
    emoji: "📢",
    description: "Aide les entreprises à créer des campagnes de réseaux sociaux cohérentes avec leur marque.",
    useCase: "Automatisation du marketing social media."
  },
  {
    name: "Google Whisk",
    icon: Film,
    emoji: "🎞️",
    description: "Utilise des images comme base pour créer des storyboards et explorer des concepts visuels.",
    useCase: "Pré-production vidéo, brainstorming créatif."
  },
  {
    name: "Google Opal",
    icon: Smartphone,
    emoji: "📲",
    description: "Convertit des descriptions textuelles en mini-apps fonctionnelles en quelques minutes.",
    useCase: "Prototypage rapide, création d'outils internes."
  },
  {
    name: "Google Lumiere",
    icon: Video,
    emoji: "🎥",
    description: "Modèle de diffusion qui crée des vidéos réalistes à partir de descriptions écrites.",
    useCase: "Production de contenus vidéo à grande échelle."
  }
];

const OutilsGoogleAIPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>12 outils Google AI à maîtriser en 2026 | Formation Intelligence Artificielle</title>
        <meta name="description" content="Découvrez les 12 outils Google AI essentiels en 2026 : Gemini, NotebookLM, Veo 3, Firebase Studio... Formation professionnelle pour maîtriser ces outils." />
        <meta name="keywords" content="formation Google AI, outils Google IA, Gemini formation, NotebookLM entreprise, formation intelligence artificielle" />
        <link rel="canonical" href="https://academie-ia.fr/formation-google-ai" />
        <meta property="og:title" content="12 outils Google AI à maîtriser en 2026" />
        <meta property="og:description" content="Guide complet des outils Google AI avec formation professionnelle." />
      </Helmet>

      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
              Guide complet 2026
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6">
              12 outils{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Google AI
              </span>{" "}
              à maîtriser
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Google a déployé un arsenal d'outils IA révolutionnaires. 
              Voici ceux que votre entreprise doit connaître — et comment nous vous formons à les utiliser.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/tunnel"
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-xl hover:opacity-90 transition-all"
              >
                <Zap className="w-5 h-5" />
                Demander une formation
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-4 bg-green-500 text-white font-bold rounded-xl hover:bg-green-600 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp direct
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#050505] to-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {googleTools.map((tool, i) => (
              <motion.div
                key={tool.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/30 transition-all group"
              >
                <div className="flex items-start gap-4 mb-4">
                  <span className="text-4xl">{tool.emoji}</span>
                  <div>
                    <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                      {tool.name}
                    </h3>
                  </div>
                </div>
                <p className="text-gray-300 mb-4">{tool.description}</p>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-sm text-blue-400">
                    <strong>Cas d'usage :</strong> {tool.useCase}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Formation */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pourquoi se former aux outils Google AI ?
            </h2>
            <p className="text-xl text-gray-400">
              Ces outils sont gratuits ou peu coûteux, mais leur maîtrise fait toute la différence.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Intégration native avec Google Workspace",
              "Écosystème unifié (même compte, mêmes données)",
              "Mises à jour constantes avec les dernières avancées",
              "Gratuité ou coût réduit vs concurrents",
              "Puissance de calcul Google Cloud",
              "Documentation et support en français"
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4"
              >
                <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                <span className="text-white">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-t from-blue-900/30 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Formez vos équipes aux outils Google AI
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              En une journée, vos collaborateurs maîtrisent ces outils et les appliquent à leurs cas concrets.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/formation-ia"
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-xl"
              >
                <Zap className="w-5 h-5" />
                Découvrir nos formations
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/10">
        <p className="text-gray-500 text-sm">
          Académie IA © {new Date().getFullYear()} — Formation Intelligence Artificielle
        </p>
      </footer>
    </div>
  );
};

export default OutilsGoogleAIPage;
